import type { IOtherFeeTypeOption } from '../models/client-form.model'

export const ACADEMIC_FEE_WARNING_THRESHOLD = 1000000

/**
 * Other fee types shown in Academic Information & lead S2.
 * UI shows `label` (e.g. "Others"); payloads use `value` (e.g. `other`, not `others`).
 */
export const OTHER_FEE_TYPE_OPTIONS: readonly IOtherFeeTypeOption[] = [
    { label: 'Monthly fees', value: 'monthly_fees' },
    { label: 'Development fee', value: 'development_fee' },
    { label: 'Smart class fee', value: 'smart_class_fee' },
    { label: 'Tuition fee', value: 'tuition_fee' },
    { label: 'Edtech fee', value: 'edtech_fee' },
    { label: 'Technical fee', value: 'technical_fee' },
    { label: 'Others', value: 'other' },
] as const

export const ACADEMIC_MESSAGES = {
    OTHER_FEE: {
        GRADES_REQUIRED: 'Please select at least one grade before adding this fee.',
        DUPLICATE_GRADES: 'This fee type and grade combination is already added.',
    },
    INVALID_AMOUNT: 'Please enter a valid amount.',
} as const

/** `curriculum_detail.grades` rows may expose the grade under any of these keys. */
export const CURRICULUM_GRADE_FIELD_KEYS = [
    'grade',
    'Grade',
    'class',
    'Class',
    'level',
    'standard',
] as const

export function formatBoardDuplicateSelectionMessage(displayBoardName: string): string {
    const boardName = displayBoardName.trim()
    if (!boardName) return ''
    return `${boardName} is already selected. Please choose another board.`
}

export function formatBoardDuplicateWithFirstBoardMessage(
    displayBoardName: string,
    firstBoardOrdinalOneBased: number,
): string {
    const boardName = displayBoardName.trim()
    if (!boardName) return ''
    return `${boardName} is already selected on Board ${firstBoardOrdinalOneBased}. Please choose another board or remove this card.`
}

export function getOtherFeeTypeLabel(feeType: string | null | undefined): string {
    const key = String(feeType ?? '').trim()
    if (!key) return ''
    if (key === 'annual_fee') {
        return 'Annual fee'
    }
    const found = OTHER_FEE_TYPE_OPTIONS.find((option) => option.value === key)
    return found?.label ?? key
}

export const STUDENT_STRENGTH_MAX = 5000

export const SCHOOL_CLIENT_TYPES = ['National School', 'International School'] as const

export const ACADEMIC_FEE_TYPE_MAP: Record<string, string> = {
    monthly_fees: 'monthly_fees',
    annual_fee: 'annual_fee',
    development_fee: 'development_fee',
    smart_class_fee: 'smart_class_fee',
    tuition_fee: 'tuition_fee',
    edtech_fee: 'edtech_fee',
    technical_fee: 'technical_fee',
    tech_fee: 'technical_fee',
    other: 'other',
    other_fee: 'other',
    others: 'other',
}

export const EMPTY_ADDRESS_VALUE = {
    addressLine1: '',
    city: '',
    pincode: '',
    state: '',
    country: '',
    phoneNumbers: '',
}

/** Matches `fee_type` stored on other-fee rows used to populate derived numeric fee fields. */
export const DERIVED_OTHER_FEE_TYPE_VALUES = {
    tuitionFee: 'tuition_fee',
    techFee: 'technical_fee',
    smartClassFee: 'smart_class_fee',
    developmentFee: 'development_fee',
} as const

export const ACADEMIC_INFORMATION_DIALOG_COPY = {
    removeBoardTitle: 'Remove board?',
    removeBoardMessagePrefix: 'Remove Board',
    removeBoardMessageSuffix:
        'All grades, annual fees and student strength data in this board will be deleted.',
    removeFeeEntryTitle: 'Remove fee entry?',
    removeFeeEntryMessage: 'Remove this fee entry? This action cannot be undone.',
    removeLeadOtherFeeTitle: 'Remove other fee?',
    removeLeadOtherFeeMessageSuffix:
        'This will remove the fee from the lead. This action cannot be undone. Are you sure you want to continue?',
    removeVendorDetailTitle: 'Remove vendor?',
    removeVendorDetailMessageSuffix:
        'This will remove the vendor from the lead. This action cannot be undone. Are you sure you want to continue?',
    confirmText: 'Remove',
    cancelText: 'Cancel',
} as const
